import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular/lazy';
import { StorageMigration } from '../plugin/storage-migration';

/**
 * https://github.com/PillowPillow/ng2-webstorage?tab=readme-ov-file#migrate-from-v13x-to-the-v18
 * Service de migration pour gérer :
 * 1. Le changement d'origine Android (https://localhost -> http://localhost)
 * 2. Le changement de format des clés ngx-webstorage
 *    Ancien format: "ngx-webstorage|xxxx" (avec | et lowercase)
 *    Nouveau format: "ngx-webstorage:XXXX" (avec : et uppercase)
 */
@Injectable({ providedIn: 'root' })
export class StorageMigrationService {
  private readonly OLD_PREFIX = 'ngx-webstorage|';
  private readonly NEW_PREFIX = 'ngx-webstorage:';

  constructor(private platform: Platform) {}

  /**
   * Effectue la migration au démarrage de l'application
   * Gère deux types de migration :
   * 1. Migration depuis l'ancienne origine (https://localhost) via le plugin natif
   *    (les données sont automatiquement supprimées après migration par le plugin)
   * 2. Migration du format des clés ngx-webstorage (ancien -> nouveau)
   */
  public async migrate(): Promise<void> {
    console.log('[StorageMigration] Vérification de la migration...');

    // Étape 1 : Récupérer les données de l'ancienne origine (uniquement sur Android)
    // Si aucune donnée n'existe, la migration sera skippée automatiquement
    if (this.platform.is('android')) {
      try {
        console.log("[StorageMigration] Récupération des données de l'ancienne origine (https://localhost)");
        await this.migrateLegacyOrigin();
      } catch (error) {
        console.error('[StorageMigration] Erreur lors de la récupération des données legacy:', error);
        // On continue même en cas d'erreur
      }
    }

    // Étape 2 : Migration du format des clés (pour les données déjà présentes)
    this.migrateKeysFormat();

    console.log(`[StorageMigration] Migration terminée`);
  }

  /**
   * Récupère les données de l'ancienne origine via le plugin natif
   * et les migre vers le nouveau localStorage avec le bon format
   */
  private async migrateLegacyOrigin(): Promise<void> {
    try {
      // Récupérer toutes les données de l'ancienne origine
      // Si aucune donnée n'existe ou si la base est corrompue, getLegacyLocalStorage retournera success=false ou count=0
      const result = await StorageMigration.getLegacyLocalStorage();

      if (!result.success) {
        console.warn('[StorageMigration] Impossible de récupérer les données legacy:', result.error);
        return;
      }

      if (!result.data || result.count === 0) {
        console.log('[StorageMigration] Aucune donnée legacy trouvée');
        return;
      }

      console.log(`[StorageMigration] ${result.count} clé(s) trouvée(s) dans l'ancienne origine`);

      // Migrer chaque clé vers le nouveau localStorage
      for (const [key, value] of Object.entries(result.data)) {
        try {
          // Vérifier si la clé n'existe pas déjà dans le nouveau localStorage
          if (!localStorage.getItem(key)) {
            localStorage.setItem(key, value);
          } else {
            console.log(`[StorageMigration] Clé "${key}" déjà présente, skip`);
          }
        } catch (error) {
          console.error(`[StorageMigration] Erreur lors de la migration de "${key}":`, error);
        }
      }

      console.log(`[StorageMigration] Clés migrées depuis l'ancienne origine (les données legacy ont été supprimées du LevelDB)`);
    } catch (error) {
      console.error('[StorageMigration] Erreur lors de la migration legacy:', error);
    }
  }

  /**
   * Migre toutes les clés  qui correspondent au pattern de l'ancien format
   */
  private migrateKeysFormat() {
    const keysToMigrate: Array<{ oldKey: string; newKey: string; value: string }> = [];

    // Parcourir toutes les clés du localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (!key) continue;

      // Vérifier si c'est une ancienne clé ngx-webstorage
      if (key.startsWith(this.OLD_PREFIX)) {
        const extractedKey = key.substring(this.OLD_PREFIX.length);
        const newKey = `${this.NEW_PREFIX}${extractedKey.toUpperCase()}`;
        const value = localStorage.getItem(key);

        if (value !== null && !localStorage.getItem(newKey)) {
          keysToMigrate.push({ oldKey: key, newKey, value });
        }
      }
    }

    // Effectuer les migrations
    for (const { oldKey, newKey, value } of keysToMigrate) {
      console.log(`[StorageMigration] Migration: "${oldKey}" -> "${newKey}"`);
      localStorage.setItem(newKey, value);
      localStorage.removeItem(oldKey);
    }
  }
}
